export const DASHBOARD_IDS = [
    'main',
    'equipment-utilization',
    'driver-activity',
    'sla',
    'ai-optimization',
    'finance-accounting',
    'sales-brokerage',
];
export const DASHBOARD_PATHS = {
    main: '/dashboard',
    'equipment-utilization': '/dashboard/equipment-utilization',
    'driver-activity': '/dashboard/driver-activity',
    sla: '/dashboard/sla',
    'ai-optimization': '/dashboard/ai-optimization',
    'finance-accounting': '/dashboard/finance',
    'sales-brokerage': '/dashboard/sales',
};
export const DASHBOARD_LABELS = {
    main: 'Main Dashboard',
    'equipment-utilization': 'Equipment Utilization',
    'driver-activity': 'Driver Activity',
    sla: 'SLA Dashboard',
    'ai-optimization': 'AI Optimization',
    'finance-accounting': 'Finance & Accounting',
    'sales-brokerage': 'Sales & Brokerage',
};
// All built-in role identifiers — keep in sync with Prisma UserRole enum.
export const DASHBOARD_ACL_ROLES = [
    'super_admin',
    'admin',
    'manager',
    'dispatcher',
    'accounting',
    'sales',
    'viewer',
    'driver',
    'owner_operator',
    'mechanic',
    'carrier',
    'customer',
];
// Widget categorisation so the permissions UI can group toggles by
// functional area (e.g. all "Finance" widgets together).
export const DASHBOARD_WIDGET_CATEGORIES = [
    'operations',
    'finance',
    'fleet',
    'customer',
    'analytics',
];
export const DASHBOARD_WIDGET_CATEGORY_LABELS = {
    operations: 'Operations',
    finance: 'Finance & Accounting',
    fleet: 'Fleet & Equipment',
    customer: 'Customer-facing',
    analytics: 'Analytics & Trends',
};
// Widget catalogue — single source of truth for the Settings UI grouping.
// Keep `id` values in sync with the widget registry on the client and with
// the redaction logic in `packages/server/.../dashboard.controller.ts`.
export const DASHBOARD_WIDGET_METAS = [
    { id: 'active-loads', label: 'Active Loads', category: 'operations' },
    { id: 'driver-utilization', label: 'Driver Utilization', category: 'fleet' },
    { id: 'on-time-delivery', label: 'On-Time Delivery %', category: 'operations' },
    { id: 'avg-rate-mile', label: 'Avg Rate / Mile', category: 'analytics' },
    { id: 'total-revenue', label: 'Total Revenue', category: 'finance' },
    { id: 'open-invoices', label: 'Open Invoices', category: 'finance' },
    { id: 'pending-settlements', label: 'Pending Settlements', category: 'finance' },
    { id: 'delivered', label: 'Delivered Loads', category: 'operations' },
    { id: 'revenue-by-month', label: 'Revenue by Month', category: 'finance' },
    { id: 'top-routes', label: 'Top Routes', category: 'analytics' },
    { id: 'revenue-by-customer', label: 'Revenue by Customer', category: 'finance' },
    { id: 'on-time-delivery-pct', label: 'On-Time Delivery Trend', category: 'analytics' },
    { id: 'load-status-distribution', label: 'Load Status Distribution', category: 'operations' },
    { id: 'load-volume', label: 'Load Volume', category: 'analytics' },
    { id: 'load-creation-sources', label: 'Loads Created This Week', category: 'operations' },
];
export const DASHBOARD_WIDGET_IDS = DASHBOARD_WIDGET_METAS.map((w) => w.id);
export function widgetsByCategory() {
    const out = {
        operations: [],
        finance: [],
        fleet: [],
        customer: [],
        analytics: [],
    };
    for (const w of DASHBOARD_WIDGET_METAS)
        out[w.category].push(w);
    return out;
}
// ─────────────────────────────────────────────────────────────────────
// Hardcoded defaults — applied when an organisation has not (yet)
// overridden the matrix via Settings. The Settings page seeds explicit
// rows on first save; until then these are authoritative.
// ─────────────────────────────────────────────────────────────────────
const ALL_DASHBOARDS = [...DASHBOARD_IDS];
const SHIPPED_SPECIALIZED_DASHBOARDS = [
    'equipment-utilization',
    'driver-activity',
    'sla',
    'ai-optimization',
];
const OPS_DASHBOARDS = ['main', 'equipment-utilization', 'driver-activity', 'sla'];
const MAIN_ONLY = ['main'];
const ALL_WIDGETS = [...DASHBOARD_WIDGET_IDS];
const FINANCE_WIDGETS = [
    'total-revenue',
    'open-invoices',
    'pending-settlements',
    'revenue-by-month',
    'revenue-by-customer',
];
const OPS_WIDGETS = [
    'active-loads',
    'driver-utilization',
    'on-time-delivery',
    'avg-rate-mile',
    'delivered',
    'top-routes',
    'on-time-delivery-pct',
    'load-status-distribution',
    'load-volume',
    'load-creation-sources',
];
const CUSTOMER_WIDGETS = ['active-loads', 'delivered', 'open-invoices'];
const ALL_ACCESS_ROLES = new Set(['super_admin', 'admin', 'owner']);
function normalizeRole(role) {
    return (role ?? '').trim().toLowerCase();
}
export function defaultAllowedDashboardsForRole(role) {
    const normalized = normalizeRole(role);
    if (ALL_ACCESS_ROLES.has(normalized))
        return [...ALL_DASHBOARDS];
    switch (normalized) {
        case 'manager':
            return ['main', ...SHIPPED_SPECIALIZED_DASHBOARDS];
        case 'dispatcher':
            return [...OPS_DASHBOARDS];
        case 'accounting':
        case 'sales':
        case 'viewer':
        case 'driver':
        case 'carrier':
        case 'customer':
        case 'mechanic':
        case 'owner_operator':
            return [...MAIN_ONLY];
        default:
            return [];
    }
}
export function defaultAllowedWidgetsForRole(role) {
    const normalized = normalizeRole(role);
    if (ALL_ACCESS_ROLES.has(normalized) || normalized === 'manager')
        return [...ALL_WIDGETS];
    switch (normalized) {
        case 'accounting':
            return [...FINANCE_WIDGETS];
        case 'dispatcher':
        case 'viewer':
        case 'mechanic':
            return [...OPS_WIDGETS];
        case 'customer':
            return [...CUSTOMER_WIDGETS];
        case 'driver':
        case 'carrier':
        case 'owner_operator':
            return ['active-loads', 'delivered'];
        case 'sales':
            return [];
        default:
            return [];
    }
}
export function resolveAllowedDashboardsForRole(role, grants) {
    const normalized = normalizeRole(role);
    const override = grants?.dashboards?.[normalized];
    if (override) {
        return override.filter((id) => DASHBOARD_IDS.includes(id));
    }
    return defaultAllowedDashboardsForRole(role);
}
export function resolveAllowedWidgetsForRole(role, grants) {
    const normalized = normalizeRole(role);
    const override = grants?.widgets?.[normalized];
    if (override) {
        return override.filter((id) => DASHBOARD_WIDGET_IDS.includes(id));
    }
    return defaultAllowedWidgetsForRole(role);
}
// ─────────────────────────────────────────────────────────────────────
// Back-compat helpers (no grants) — keep the original signatures so
// existing callers (client route guards, sidebar filters) continue to
// work. New call sites should prefer the `resolve…` variants and pass
// grants loaded from the server.
// ─────────────────────────────────────────────────────────────────────
export function allowedDashboardsForRole(role) {
    return defaultAllowedDashboardsForRole(role);
}
export function allowedWidgetsForRole(role) {
    return defaultAllowedWidgetsForRole(role);
}
export function defaultDashboardForRole(role) {
    const allowed = allowedDashboardsForRole(role);
    return allowed[0] ?? 'main';
}
export function canAccessDashboard(role, dashboardId) {
    return allowedDashboardsForRole(role).includes(dashboardId);
}
export function canViewDashboardWidget(role, widgetId) {
    return allowedWidgetsForRole(role).includes(widgetId);
}
export function canViewDashboardWidgetWithGrants(role, widgetId, grants) {
    return resolveAllowedWidgetsForRole(role, grants).includes(widgetId);
}
export function canAccessDashboardWithGrants(role, dashboardId, grants) {
    return resolveAllowedDashboardsForRole(role, grants).includes(dashboardId);
}
export function dashboardIdFromPath(pathname) {
    const normalized = pathname.replace(/\/+$/, '') || '/';
    const match = Object.entries(DASHBOARD_PATHS).find(([, path]) => path === normalized);
    return match?.[0] ?? null;
}
export function isFinanceDashboardWidget(widgetId) {
    return FINANCE_WIDGETS.includes(widgetId);
}
//# sourceMappingURL=dashboard-acl.js.map